import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Component from '../../components/Component';
import {
  createFanLoungePlayer,
  createFanLoungePlayerMultipart,
  getFanLoungeDashboard,
  getFanLoungePayload,
  isFanLoungeApiSuccess,
  listFanLoungePlayers,
  removeFanLoungePlayer,
  reorderFanLoungePlayers,
  updateFanLoungePlayer,
  updateFanLoungePlayerMultipart,
  uploadFanLoungeImage,
} from '../../services/fanLoungeApi';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import EditIcon from '@mui/icons-material/Edit';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

const ROLE_OPTIONS = [
  { value: 'bat', label: 'Batsman' },
  { value: 'bowl', label: 'Bowler' },
  { value: 'all', label: 'All-rounder' },
  { value: 'wk', label: 'Wicket-keeper' },
];

const emptyForm = {
  player_id: '',
  title: '',
  short_name: '',
  name: '',
  role: 'bat',
  playing11: true,
  substitute: false,
  sort_order: '0',
  image_url: '',
  logo_url: '',
};

function isTeamEditable(team, defaultTeamId) {
  if (!team) return false;
  if (team.is_editable_static_team) return true;
  return Number(team.team_id) === Number(defaultTeamId);
}

function playersForTeam(allPlayers, teamId) {
  if (!Array.isArray(allPlayers)) return [];
  return allPlayers
    .filter((p) => Number(p.team_id) === Number(teamId))
    .sort((a, b) => (a.sort_position ?? 0) - (b.sort_position ?? 0));
}

function FanLoungeDashboard() {
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [error, setError] = useState('');
  const [dashboard, setDashboard] = useState(null);
  const [players, setPlayers] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [helpExpanded, setHelpExpanded] = useState(false);
  const [dragPlayerId, setDragPlayerId] = useState(null);
  const [dragOverPlayerId, setDragOverPlayerId] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSessionExpired = useCallback(
    (res) => {
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return true;
      }
      return false;
    },
    [showSnackbar]
  );

  const handleApiError = useCallback(
    (res, fallback) => {
      if (handleSessionExpired(res)) return true;
      if (!isFanLoungeApiSuccess(res)) {
        setError(res?.message || fallback);
        return true;
      }
      return false;
    },
    [handleSessionExpired]
  );

  const config = dashboard?.config;
  const sorting = dashboard?.sorting;
  const competition = dashboard?.competition;
  const match = dashboard?.match;
  const teams = dashboard?.teams || [];
  const counts = dashboard?.counts;

  const selectedTeam = useMemo(
    () => teams.find((t) => Number(t.team_id) === Number(selectedTeamId)),
    [teams, selectedTeamId]
  );

  const canEdit = isTeamEditable(selectedTeam, config?.default_team_id);

  const applyPlayersList = useCallback((list) => {
    setPlayers(
      [...list].sort((a, b) => (a.sort_position ?? 0) - (b.sort_position ?? 0))
    );
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getFanLoungeDashboard();
      if (handleApiError(res, 'Failed to load Fan Lounge dashboard.')) {
        setDashboard(null);
        setPlayers([]);
        return;
      }
      const data = getFanLoungePayload(res);
      setDashboard(data);
      const defaultTeam = data?.config?.default_team_id;
      setSelectedTeamId((prev) => {
        const teamId = prev ?? defaultTeam;
        const teamPlayers = playersForTeam(data?.players, teamId);
        applyPlayersList(teamPlayers);
        return prev ?? defaultTeam;
      });
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to load Fan Lounge dashboard.');
      setDashboard(null);
      setPlayers([]);
    } finally {
      setLoading(false);
    }
  }, [applyPlayersList, handleApiError]);

  const refreshTeamPlayers = useCallback(
    async (teamId) => {
      const tid = teamId ?? selectedTeamId ?? config?.default_team_id;
      if (tid == null) return;
      try {
        const res = await listFanLoungePlayers({ teamId: tid });
        if (handleApiError(res, 'Failed to refresh players.')) return;
        const data = getFanLoungePayload(res);
        applyPlayersList(data?.players || []);
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to refresh players.');
      }
    },
    [applyPlayersList, config?.default_team_id, handleApiError, selectedTeamId]
  );

  useEffect(() => {
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTeamId == null) return;
    refreshTeamPlayers(selectedTeamId);
  }, [selectedTeamId, refreshTeamPlayers]);

  const handleTeamChange = (event) => {
    setSelectedTeamId(Number(event.target.value));
  };

  const persistReorder = useCallback(
    async (orderedIds) => {
      if (!canEdit || !selectedTeamId) return;
      setReordering(true);
      setError('');
      try {
        const res = await reorderFanLoungePlayers({
          team_id: selectedTeamId,
          player_ids: orderedIds,
        });
        if (handleApiError(res, 'Failed to reorder players.')) return;
        const data = getFanLoungePayload(res);
        applyPlayersList(data?.players || []);
        showSnackbar('Player order updated.');
        await fetchDashboard();
      } catch (fetchError) {
        setError(fetchError.message || 'Failed to reorder players.');
      } finally {
        setReordering(false);
      }
    },
    [
      applyPlayersList,
      canEdit,
      fetchDashboard,
      handleApiError,
      selectedTeamId,
      showSnackbar,
    ]
  );

  const movePlayer = useCallback(
    (fromIndex, toIndex) => {
      if (!canEdit || fromIndex === toIndex || toIndex < 0 || toIndex >= players.length) {
        return;
      }
      const next = [...players];
      const [removed] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, removed);
      applyPlayersList(next);
      persistReorder(next.map((p) => p.player_id));
    },
    [applyPlayersList, canEdit, persistReorder, players]
  );

  const handleDragStart = (playerId) => {
    if (!canEdit) return;
    setDragPlayerId(playerId);
  };

  const handleDragOver = (event, playerId) => {
    if (!canEdit) return;
    event.preventDefault();
    setDragOverPlayerId(playerId);
  };

  const handleDrop = (targetPlayerId) => {
    if (!canEdit || dragPlayerId == null || dragPlayerId === targetPlayerId) {
      setDragPlayerId(null);
      setDragOverPlayerId(null);
      return;
    }
    const fromIndex = players.findIndex((p) => p.player_id === dragPlayerId);
    const toIndex = players.findIndex((p) => p.player_id === targetPlayerId);
    setDragPlayerId(null);
    setDragOverPlayerId(null);
    if (fromIndex < 0 || toIndex < 0) return;
    movePlayer(fromIndex, toIndex);
  };

  const updateForm = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOpenCreate = () => {
    setEditingPlayer(null);
    setFormData({
      ...emptyForm,
      sort_order: String(players.length),
    });
    setImageFile(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (player) => {
    setEditingPlayer(player);
    setImageFile(null);
    setFormData({
      player_id: String(player.player_id ?? ''),
      title: player.title || player.name || '',
      short_name: player.short_name || '',
      name: player.name || '',
      role: player.role || 'bat',
      playing11: Boolean(player.playing11),
      substitute: Boolean(player.substitute),
      sort_order: String(player.sort_order ?? player.index ?? 0),
      image_url: player.image_url || '',
      logo_url: player.player_logo_url || '',
    });
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingPlayer(null);
    setImageFile(null);
  };

  const buildPlayerBody = () => {
    const body = {
      team_id: selectedTeamId,
      title: formData.title.trim(),
      short_name: formData.short_name.trim() || undefined,
      name: formData.name.trim() || undefined,
      role: formData.role || 'bat',
      playing11: formData.playing11,
      substitute: formData.substitute,
      sort_order: Number(formData.sort_order),
    };
    if (formData.image_url.trim()) body.image_url = formData.image_url.trim();
    if (formData.logo_url.trim()) body.logo_url = formData.logo_url.trim();
    return body;
  };

  const handleUploadImage = async () => {
    if (!imageFile) {
      showSnackbar('Choose an image file first.', 'warning');
      return;
    }
    setUploadingImage(true);
    try {
      const res = await uploadFanLoungeImage(imageFile);
      if (handleSessionExpired(res) || !isFanLoungeApiSuccess(res)) {
        showSnackbar(res?.message || 'Image upload failed.', 'error');
        return;
      }
      const url = getFanLoungePayload(res)?.imageUrl;
      if (url) {
        updateForm('image_url', url);
        showSnackbar('Image uploaded.');
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSavePlayer = async () => {
    if (!canEdit) return;
    const title = formData.title.trim();
    if (!title) {
      showSnackbar('Title is required.', 'warning');
      return;
    }
    if (!editingPlayer && !formData.player_id.trim()) {
      showSnackbar('Player ID is required.', 'warning');
      return;
    }

    setSaving(true);
    setError('');
    try {
      let res;
      if (imageFile) {
        const fd = new FormData();
        fd.append('team_id', String(selectedTeamId));
        fd.append('title', title);
        if (formData.short_name.trim()) fd.append('short_name', formData.short_name.trim());
        if (formData.name.trim()) fd.append('name', formData.name.trim());
        fd.append('role', formData.role);
        fd.append('playing11', String(formData.playing11));
        fd.append('substitute', String(formData.substitute));
        fd.append('sort_order', formData.sort_order);
        if (formData.logo_url.trim()) fd.append('logo_url', formData.logo_url.trim());
        if (formData.image_url.trim() && !imageFile) fd.append('image_url', formData.image_url.trim());
        fd.append('image', imageFile);

        if (editingPlayer) {
          res = await updateFanLoungePlayerMultipart(editingPlayer.player_id, fd);
        } else {
          fd.append('player_id', formData.player_id.trim());
          res = await createFanLoungePlayerMultipart(fd);
        }
      } else {
        const body = buildPlayerBody();
        if (editingPlayer) {
          res = await updateFanLoungePlayer(editingPlayer.player_id, body);
        } else {
          res = await createFanLoungePlayer({
            ...body,
            player_id: Number(formData.player_id.trim()),
          });
        }
      }

      if (handleApiError(res, 'Failed to save player.')) return;
      showSnackbar(editingPlayer ? 'Player updated.' : 'Player added.');
      handleCloseDrawer();
      await refreshTeamPlayers(selectedTeamId);
      await fetchDashboard();
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to save player.');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget || !canEdit) return;
    setDeleting(true);
    setError('');
    try {
      const res = await removeFanLoungePlayer(deleteTarget.player_id);
      if (handleApiError(res, 'Failed to remove player.')) return;
      showSnackbar('Player removed from squad.');
      setDeleteTarget(null);
      await refreshTeamPlayers(selectedTeamId);
      await fetchDashboard();
    } catch (fetchError) {
      setError(fetchError.message || 'Failed to remove player.');
    } finally {
      setDeleting(false);
    }
  };

  const matchTitle = match?.title || match?.match_title || 'Static match';
  const competitionTitle = competition?.title || 'Fan Lounge';

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center flex-wrap">
                  <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/">
                    <ArrowBackIcon />
                  </IconButton>
                  <Box sx={{ ml: 2.5 }}>
                    <h3 style={{ margin: 0 }}>Fan Lounge — Daily Cricket Talk</h3>
                    <Typography variant="body2" color="text.secondary">
                      Static squad for competition cid {config?.cid ?? '—'}, match{' '}
                      {config?.match_id ?? '—'}
                    </Typography>
                  </Box>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <Card>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {competitionTitle}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {matchTitle}
                        {match?.teama__name && match?.teamb__name
                          ? ` · ${match.teama__name} vs ${match.teamb__name}`
                          : ''}
                      </Typography>
                      {counts && (
                        <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip size="small" label={`${counts.total_players} players`} />
                          <Chip
                            size="small"
                            color="success"
                            variant="outlined"
                            label={`${counts.playing11} playing XI`}
                          />
                          <Chip
                            size="small"
                            variant="outlined"
                            label={`${counts.substitutes} substitutes`}
                          />
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
                        onClick={fetchDashboard}
                        disabled={loading || reordering}
                      >
                        Refresh
                      </Button>
                      {canEdit && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={handleOpenCreate}
                          disabled={loading}
                        >
                          Add player
                        </Button>
                      )}
                    </Box>
                  </Box>

                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Team"
                        value={selectedTeamId ?? ''}
                        onChange={handleTeamChange}
                        disabled={loading || !teams.length}
                      >
                        {teams.map((team) => (
                          <MenuItem key={team.team_id} value={team.team_id}>
                            {team.name || team.short_name || team.team_id}
                            {team.is_editable_static_team ? ' (editable)' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      {!canEdit && selectedTeam && (
                        <Alert severity="info" sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                          This team is read-only. Select the static editable team to manage players.
                        </Alert>
                      )}
                    </Grid>
                  </Grid>

                  <Paper variant="outlined" sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        px: 2,
                        py: 1,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'grey.50',
                        cursor: 'pointer',
                      }}
                      onClick={() => setHelpExpanded((v) => !v)}
                    >
                      <Typography variant="subtitle2">Sort order help</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {helpExpanded ? 'Hide' : 'Show'}
                      </Typography>
                    </Box>
                    <Collapse in={helpExpanded}>
                      <Box sx={{ p: 2 }}>
                        {sorting ? (
                          <>
                            <Typography variant="body2" gutterBottom>
                              <strong>Primary:</strong> {sorting.primary_field} ({sorting.direction})
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {sorting.description}
                            </Typography>
                            <Typography variant="body2">
                              <strong>Fallback:</strong> {sorting.fallback_field}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {sorting.fallback_when}
                            </Typography>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Load the dashboard to see sorting metadata from the API.
                          </Typography>
                        )}
                        {canEdit && (
                          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                            Drag rows or use arrows to reorder. Changes call PATCH /players/reorder.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Paper>

                  {loading && !players.length ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {canEdit && <TableCell width={48} />}
                            <TableCell width={48}>#</TableCell>
                            <TableCell width={56}>Avatar</TableCell>
                            <TableCell>Name</TableCell>
                            <TableCell>Short name</TableCell>
                            <TableCell>Role</TableCell>
                            <TableCell>Squad</TableCell>
                            <TableCell>Player ID</TableCell>
                            {canEdit && <TableCell align="right">Actions</TableCell>}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {players.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={canEdit ? 9 : 7} align="center" sx={{ py: 4 }}>
                                <Typography variant="body2" color="text.secondary">
                                  No players for this team.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : (
                            players.map((player, index) => {
                              const isDragOver = dragOverPlayerId === player.player_id;
                              return (
                                <TableRow
                                  key={player.player_id}
                                  hover
                                  draggable={canEdit && !reordering}
                                  onDragStart={() => handleDragStart(player.player_id)}
                                  onDragEnd={() => {
                                    setDragPlayerId(null);
                                    setDragOverPlayerId(null);
                                  }}
                                  onDragOver={(e) => handleDragOver(e, player.player_id)}
                                  onDrop={() => handleDrop(player.player_id)}
                                  sx={{
                                    opacity: reordering ? 0.7 : 1,
                                    bgcolor: isDragOver ? 'action.hover' : undefined,
                                    cursor: canEdit ? 'grab' : 'default',
                                  }}
                                >
                                  {canEdit && (
                                    <TableCell>
                                      <DragIndicatorIcon fontSize="small" color="disabled" />
                                    </TableCell>
                                  )}
                                  <TableCell>{player.sort_position ?? index + 1}</TableCell>
                                  <TableCell>
                                    <Avatar
                                      src={player.image_url || player.player_logo_url || undefined}
                                      alt={player.title || player.name || ''}
                                      sx={{ width: 36, height: 36 }}
                                    >
                                      {(player.short_name || player.title || '?')[0]}
                                    </Avatar>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontWeight={600}>
                                      {player.title || player.name || '—'}
                                    </Typography>
                                    {player.name && player.name !== player.title && (
                                      <Typography variant="caption" color="text.secondary">
                                        {player.name}
                                      </Typography>
                                    )}
                                  </TableCell>
                                  <TableCell>{player.short_name || '—'}</TableCell>
                                  <TableCell>
                                    <Chip label={player.role_str || player.role || '—'} size="small" />
                                  </TableCell>
                                  <TableCell>
                                    {player.playing11 ? (
                                      <Chip label="Playing XI" size="small" color="success" />
                                    ) : player.substitute ? (
                                      <Chip label="Substitute" size="small" color="warning" />
                                    ) : (
                                      <Chip label="Bench" size="small" variant="outlined" />
                                    )}
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" fontFamily="monospace">
                                      {player.player_id}
                                    </Typography>
                                  </TableCell>
                                  {canEdit && (
                                    <TableCell align="right">
                                      <Tooltip title="Move up">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={index === 0 || reordering}
                                            onClick={() => movePlayer(index, index - 1)}
                                          >
                                            <KeyboardArrowUpIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <Tooltip title="Move down">
                                        <span>
                                          <IconButton
                                            size="small"
                                            disabled={index === players.length - 1 || reordering}
                                            onClick={() => movePlayer(index, index + 1)}
                                          >
                                            <KeyboardArrowDownIcon fontSize="small" />
                                          </IconButton>
                                        </span>
                                      </Tooltip>
                                      <IconButton
                                        size="small"
                                        onClick={() => handleOpenEdit(player)}
                                        aria-label="Edit"
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        onClick={() => setDeleteTarget(player)}
                                        aria-label="Remove"
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}

                  {reordering && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Saving order…
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      <Drawer anchor="right" open={drawerOpen} onClose={handleCloseDrawer} PaperProps={{ sx: { width: { xs: '100%', sm: 420 } } }}>
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            {editingPlayer ? 'Edit player' : 'Add player'}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
            Use static player IDs (e.g. 99xxx). Image can be uploaded or pasted as URL.
          </Typography>

          {!editingPlayer && (
            <TextField
              fullWidth
              size="small"
              label="Player ID"
              value={formData.player_id}
              onChange={(e) => updateForm('player_id', e.target.value)}
              sx={{ mb: 2 }}
              required
            />
          )}

          <TextField
            fullWidth
            size="small"
            label="Title"
            value={formData.title}
            onChange={(e) => updateForm('title', e.target.value)}
            sx={{ mb: 2 }}
            required
          />
          <TextField
            fullWidth
            size="small"
            label="Short name"
            value={formData.short_name}
            onChange={(e) => updateForm('short_name', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Playing eleven name"
            value={formData.name}
            onChange={(e) => updateForm('name', e.target.value)}
            helperText="Optional; defaults to title"
            sx={{ mb: 2 }}
          />
          <TextField
            select
            fullWidth
            size="small"
            label="Role"
            value={formData.role}
            onChange={(e) => updateForm('role', e.target.value)}
            sx={{ mb: 2 }}
          >
            {ROLE_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            fullWidth
            size="small"
            label="Sort order (0-based)"
            type="number"
            value={formData.sort_order}
            onChange={(e) => updateForm('sort_order', e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Image URL"
            value={formData.image_url}
            onChange={(e) => updateForm('image_url', e.target.value)}
            sx={{ mb: 1 }}
          />
          <TextField
            fullWidth
            size="small"
            label="Logo URL"
            value={formData.logo_url}
            onChange={(e) => updateForm('logo_url', e.target.value)}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" component="label" size="small">
              Choose image
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={(e) => setImageFile(e.target.files?.[0] || null)}
              />
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleUploadImage}
              disabled={!imageFile || uploadingImage}
            >
              {uploadingImage ? 'Uploading…' : 'Upload to S3'}
            </Button>
          </Box>
          {imageFile && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              File selected: {imageFile.name} (will be sent on save if not uploaded)
            </Typography>
          )}

          <FormControlLabel
            control={
              <Switch
                checked={formData.playing11}
                onChange={(e) => updateForm('playing11', e.target.checked)}
              />
            }
            label="Playing XI"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formData.substitute}
                onChange={(e) => updateForm('substitute', e.target.checked)}
              />
            }
            label="Substitute"
          />

          <Box sx={{ display: 'flex', gap: 1, mt: 3 }}>
            <Button variant="outlined" onClick={handleCloseDrawer} disabled={saving}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSavePlayer} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => !deleting && setDeleteTarget(null)}>
        <DialogTitle>Remove from squad?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove{' '}
            <strong>{deleteTarget?.title || deleteTarget?.name || deleteTarget?.player_id}</strong>{' '}
            from the playing squad? This does not delete the global player record.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={deleting}>
            {deleting ? 'Removing…' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default FanLoungeDashboard;
